/**
 * Hostel + School + Class + Section + Room + Bed picker.
 * - When `hostel_id` is picked, the school list is filtered to only the schools
 *   mapped to that hostel (via GET /api/admin/hostels/{hostel_id}/schools).
 * - When `school_id` + `class_name` + `section` are picked, displays live
 *   availability vs capacity from /api/admin/class-rooms.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MasterSelectOrInput } from "@/components/admissions/MasterSelectOrInput";

function useFilteredSchools(hostelId, allSchools) {
  const [filtered, setFiltered] = useState(allSchools);
  useEffect(() => {
    if (!hostelId) { setFiltered(allSchools); return; }
    api.get(`/admin/hostels/${hostelId}/schools`)
      .then((r) => setFiltered(r.data?.length ? r.data : allSchools))
      .catch(() => setFiltered(allSchools));
  }, [hostelId, allSchools]);
  return filtered;
}

function useClassRooms(schoolId, hostelId) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!schoolId && !hostelId) { setRows([]); return; }
    const params = [];
    if (schoolId) params.push(`school_id=${schoolId}`);
    if (hostelId) params.push(`hostel_id=${hostelId}`);
    api.get(`/admin/class-rooms?${params.join("&")}`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [schoolId, hostelId]);
  return rows;
}

function AvailabilityBadge({ matchedRoom }) {
  if (!matchedRoom) return null;
  if (matchedRoom.available > 0) {
    return (
      <Badge variant="outline" className="border-[var(--vs-success)]/40 text-[var(--vs-success)] text-[10px]">
        {matchedRoom.available} seat{matchedRoom.available !== 1 ? "s" : ""} free
      </Badge>
    );
  }
  return <Badge variant="outline" className="border-[var(--vs-danger)]/40 text-[var(--vs-danger)] text-[10px]">FULL</Badge>;
}

export function AllocationSection({ form, set, hostels, schools, idPrefix = "adm", requireRequired = false }) {
  const filteredSchools = useFilteredSchools(form.hostel_id, schools);
  const classRooms = useClassRooms(form.school_id, form.hostel_id);

  const classOptions = Array.from(new Set(classRooms.map((c) => c.class_name).filter(Boolean))).sort();
  const sectionOptions = Array.from(new Set(
    classRooms.filter((c) => c.class_name === form.class_name).map((c) => c.section).filter(Boolean)
  )).sort();
  const roomOptions = Array.from(new Set(
    classRooms
      .filter((c) => c.class_name === form.class_name && (!form.section || c.section === form.section))
      .map((c) => c.room_no)
      .filter(Boolean)
  )).sort();

  const matchedRoom = classRooms.find((c) =>
    c.class_name === form.class_name &&
    (!form.section || c.section === form.section) &&
    (!form.room_no || c.room_no === form.room_no)
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label>Hostel {requireRequired && <span className="text-[10px] text-[var(--vs-text-secondary)] font-normal">(hostel OR school *)</span>}</Label>
        <Select value={form.hostel_id || "none"} onValueChange={(v) => set("hostel_id", v === "none" ? "" : v)}>
          <SelectTrigger data-testid={`${idPrefix}-hostel`} className="bg-white"><SelectValue placeholder="Select hostel" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {hostels.map((h) => (
              <SelectItem key={h.id} value={h.id}>
                {h.name}{h.status && h.status !== "active" ? ` (${h.status})` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>
          School
          {form.hostel_id && <span className="text-[10px] text-[var(--vs-text-secondary)] font-normal ml-1">(filtered by hostel mapping)</span>}
        </Label>
        <Select value={form.school_id || "none"} onValueChange={(v) => set("school_id", v === "none" ? "" : v)}>
          <SelectTrigger data-testid={`${idPrefix}-school`} className="bg-white"><SelectValue placeholder="Select school" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {filteredSchools.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}{s.status && s.status !== "active" ? ` (${s.status})` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>
          Class{requireRequired ? " *" : ""}
          <span className="ml-2 inline-block"><AvailabilityBadge matchedRoom={matchedRoom} /></span>
        </Label>
        <MasterSelectOrInput
          options={classOptions}
          value={form.class_name}
          onChange={(v) => set("class_name", v)}
          testId={`${idPrefix}-class`}
          placeholder={classOptions.length ? "Pick class" : "No class masters — type free-form"}
          optionLabel={(c) => `Class ${c}`}
        />
      </div>
      <div>
        <Label>Section</Label>
        <MasterSelectOrInput
          options={sectionOptions}
          value={form.section}
          onChange={(v) => set("section", v)}
          testId={`${idPrefix}-section`}
          placeholder="Pick section"
        />
      </div>
      <div>
        <Label>Room #</Label>
        <MasterSelectOrInput
          options={roomOptions}
          value={form.room_no}
          onChange={(v) => set("room_no", v)}
          testId={`${idPrefix}-room`}
          placeholder="Pick room"
          optionLabel={(r) => `Room ${r}`}
        />
      </div>
      <div>
        <Label>Bed #</Label>
        <Input
          data-testid={`${idPrefix}-bed`}
          value={form.bed_no || ""}
          onChange={(e) => set("bed_no", e.target.value)}
          className="bg-white"
        />
      </div>
    </div>
  );
}
